/* eslint-disable sonarjs/no-duplicate-string */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { KeyValue } from '@angular/common';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { LanguageService } from '@fcom/one-upgrade/../ui-translate';

import { ModalButtons, NotificationLayout, NotificationTheme, TabTheme, TabLayoutType } from '@fcom/ui-components';

import { getNumberOfFlightsInBound } from '../../utils';
import { getCabinFromUpgradeName, getUpgradeByCabin } from '../../utils/data.utils';
import { Balance, ExtendedFlightBasket } from '../../interfaces';
import { getBasketPassengerIds, getOriginalBalance } from '../../store/selectors';
import { PaymentOptionV2 } from '../../interfaces/payment-option.interface';
import { PaymentType } from '../../interfaces/payment-type.interface';
import { PaymentChangeEvent } from '../../interfaces/payment-change-event.interface';
import { State } from '../../interfaces/store.interface';
import {
  Cabin,
  Flight,
  Flights,
  FlightsV2,
  FlightV2,
  PaymentOptionsV2,
  Upgrade,
  UpgradeProduct,
  UpgradeStatus,
  UpgradeStatusReason,
} from '../../api/models';
import { sortByPaymentType, UpgradePaymentService } from '../../services/upgrade-payment.service';
import { ModalActions } from '../../store/actions';

type insufficientSeats =
  | 'oneUpgrade.notifications.insufficientSeats.singular'
  | 'oneUpgrade.notifications.insufficientSeats.plural';

interface TabSettings {
  [Cabin.Ecopremium]: {
    title: string;
    subtitle: string;
    active: boolean;
  };
  [Cabin.Business]: {
    title: string;
    subtitle: string;
    active: boolean;
  };
}

interface NotificationSettings {
  text: string;
  icon: SvgLibraryIcon;
  theme: NotificationTheme;
  numberOfSeats?: number;
}

enum TooltipType {
  CantCombine = 'cantCombine',
  NotEnoughPoints = 'notEnoughPoints',
  NotEnoughVouchers = 'notEnoughVouchers',
  NoCardPayment = 'noCardPayment',
  NotEnoughLongHaulVouchersForPax = 'notEnoughLongHaulVouchersForPax',
}

@Component({
  selector: 'fin-upgrade-bounds',
  templateUrl: 'upgrade-bounds.component.html',
  styleUrls: ['upgrade-bounds.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradeBoundsComponent implements OnInit {
  readonly TabTheme = TabTheme;
  readonly TabLayoutType = TabLayoutType;
  readonly UpgradeStatus = UpgradeStatus;
  readonly PaymentType = PaymentType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly Cabin = Cabin;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TooltipType = TooltipType;

  @Input()
  set bounds(_bounds: FlightsV2) {
    if (_bounds !== this._bounds) {
      this._bounds = _bounds;
      this.cd.markForCheck();
    }
  }

  get bounds(): FlightsV2 {
    return this._bounds;
  }

  @Input()
  set paymentForm(_paymentForm: UntypedFormGroup) {
    if (_paymentForm !== this._paymentForm) {
      this._paymentForm = _paymentForm;
      this.cd.markForCheck();
    }
  }

  get paymentForm(): UntypedFormGroup {
    return this._paymentForm;
  }

  @Input() selectedFlights: ExtendedFlightBasket[];

  @Output()
  changeTabMethod: EventEmitter<{ flightId: string; cabin: Cabin }> = new EventEmitter();

  @Output()
  changePaymentMethod: EventEmitter<PaymentChangeEvent> = new EventEmitter();

  passengers$: Observable<number>;

  originalBalance$: Observable<Balance>;

  activeTab: Record<string, Cabin> = {};

  private _bounds: Flights;
  private _paymentForm: UntypedFormGroup;

  constructor(
    private upgradePaymentService: UpgradePaymentService,
    private store$: Store<State>,
    private cd: ChangeDetectorRef,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.originalBalance$ = this.store$.pipe(select(getOriginalBalance));
    this.passengers$ = this.store$.pipe(
      select(getBasketPassengerIds),
      map((x) => x.length),
      distinctUntilChanged(),
      tap(() => this.cd.markForCheck())
    );
  }

  getNumberOfFlights(flight: FlightV2): number {
    return getNumberOfFlightsInBound(flight, this.bounds);
  }

  showPaymentMethods(cabin: Cabin, bound: FlightV2): Observable<boolean> {
    const upgrade = getUpgradeByCabin(cabin, bound.upgrades);
    const hasAvailableUpgrade = upgrade?.upgradeStatus === UpgradeStatus.Available;

    if (!hasAvailableUpgrade) {
      return of(false);
    }

    if (upgrade.isWaitlist) {
      return of(true);
    }

    return this.hasEnoughSeats(upgrade);
  }

  hasEnoughSeats(upgrade: Upgrade): Observable<boolean> {
    return this.passengers$.pipe(
      map((count) => {
        return !!upgrade.seatsAvailable && count <= upgrade.seatsAvailable;
      })
    );
  }

  printPaymentValue(flight: FlightV2, productName: UpgradeProduct, paymentType: PaymentType): Observable<string> {
    return this.upgradePaymentService.getValueForPaymentOption(flight, productName, paymentType);
  }

  trackPayments(id: string, i: number): AbstractControl {
    return (this.paymentForm.controls[id] as UntypedFormArray).controls[i];
  }

  getTabSettings(flight: FlightV2): Observable<TabSettings> {
    const premiumEconomyAvailable = getUpgradeByCabin(Cabin.Ecopremium, flight.upgrades);
    const businessAvailable = getUpgradeByCabin(Cabin.Business, flight.upgrades);
    const currentFlight = this.selectedFlights?.find(
      (selectedFlight) => selectedFlight.flightId === flight.segmentInfo.flightId
    );
    let enabledTab = currentFlight ? currentFlight.cabin : Cabin.Business;

    if (premiumEconomyAvailable && !businessAvailable) {
      enabledTab = Cabin.Ecopremium;
    }

    const selectedPaymentOption$ = currentFlight
      ? this.printPaymentValue(flight, currentFlight.productName, currentFlight?.paymentType)
      : of('-');

    const statusMap = {
      [UpgradeStatus.NotAvailable]: this.languageService.translate('oneUpgrade.tabs.notAvailable'),
      [UpgradeStatus.Waitlisted]: this.languageService.translate('oneUpgrade.tabs.waitlisted'),
      [UpgradeStatus.Success]: this.languageService.translate('oneUpgrade.tabs.confirmed'),
      [UpgradeStatus.Failed]: this.languageService.translate('oneUpgrade.tabs.failed'),
    };

    const peSubtitle$: Observable<string> = statusMap[premiumEconomyAvailable?.upgradeStatus] || of(null);
    const businessSubtitle$: Observable<string> = statusMap[businessAvailable?.upgradeStatus] || of(null);

    return combineLatest([selectedPaymentOption$, peSubtitle$, businessSubtitle$]).pipe(
      map(([selectedPaymentOption, peSubtitle, businessSubtitle]) => {
        const selectedPaymentOptionPe = enabledTab === Cabin.Ecopremium ? selectedPaymentOption : '-';
        const selectedPaymentOptionBusiness = enabledTab === Cabin.Business ? selectedPaymentOption : '-';
        const flightId = flight.segmentInfo.flightId;

        return {
          [Cabin.Ecopremium]: {
            title: `oneUpgrade.tabs.${Cabin.Ecopremium.toLowerCase()}`,
            subtitle: peSubtitle || selectedPaymentOptionPe,
            active: this.activeTab[flightId]
              ? this.activeTab[flightId] === Cabin.Ecopremium
              : enabledTab === Cabin.Ecopremium,
          },
          [Cabin.Business]: {
            title: `oneUpgrade.tabs.${Cabin.Business.toLowerCase()}`,
            subtitle: businessSubtitle || selectedPaymentOptionBusiness,
            active: this.activeTab[flightId]
              ? this.activeTab[flightId] === Cabin.Business
              : enabledTab === Cabin.Business,
          },
        };
      })
    );
  }

  getUpgradeStatusNotificationConfig(cabin: Cabin, flight: FlightV2, numberOfPassengers: number): NotificationSettings {
    const upgrade = getUpgradeByCabin(cabin, flight.upgrades);

    if (!upgrade || upgrade.upgradeStatus === UpgradeStatus.NotAvailable) {
      return {
        icon: SvgLibraryIcon.INFO,
        text: this.getNotAvailableReason(upgrade),
        theme: NotificationTheme.INFO,
      };
    }

    if (upgrade.upgradeStatus === UpgradeStatus.Success) {
      return {
        icon: SvgLibraryIcon.CHECKMARK,
        text: 'oneUpgrade.notifications.flightUpgraded',
        theme: NotificationTheme.SUCCESS,
      };
    }

    if (upgrade.upgradeStatus === UpgradeStatus.Failed) {
      return {
        icon: SvgLibraryIcon.ALERT_BLOCK,
        text: 'oneUpgrade.notifications.upgradeFailed',
        theme: NotificationTheme.ALERT,
      };
    }

    if (upgrade.upgradeStatus === UpgradeStatus.Waitlisted) {
      const text =
        cabin === Cabin.Ecopremium
          ? 'oneUpgrade.review.waitlistedNotificationPremiumEconomy'
          : 'oneUpgrade.review.waitlistedNotificationBusiness';

      return {
        icon: SvgLibraryIcon.INFO,
        text,
        theme: NotificationTheme.INFO,
      };
    }

    if (upgrade.seatsAvailable && numberOfPassengers > upgrade.seatsAvailable) {
      return {
        icon: SvgLibraryIcon.INFO,
        text: this.getSeatsAvailableText(upgrade),
        theme: NotificationTheme.INFO,
        numberOfSeats: upgrade.seatsAvailable,
      };
    }

    return {
      icon: SvgLibraryIcon.INFO,
      text: 'oneUpgrade.notifications.notAvailableForFlight',
      theme: NotificationTheme.ALERT,
    };
  }

  getNotAvailableReason(upgrade?: Upgrade): string {
    const upgradeClosedErr = 'oneUpgrade.notifications.upgradeClosed';
    const notFinnarFlightErr = 'oneUpgrade.notifications.notFinnair';
    const notAvailableErr = 'oneUpgrade.notifications.notAvailableForFlight';

    if (upgrade) {
      const errorMap = {
        [UpgradeStatusReason.TooCloseToDeparture]: upgradeClosedErr,
        [UpgradeStatusReason.NotFinnairFlight]: notFinnarFlightErr,
        [UpgradeStatusReason.SsrInBooking]: notAvailableErr,
        [UpgradeStatusReason.TooFarToDeparture]: notAvailableErr,
        [UpgradeStatusReason.TooCloseToDepartureForWaitlist]: notAvailableErr,
        [UpgradeStatusReason.SegmentInRequestedUpgradeCabin]: notAvailableErr,
        [UpgradeStatusReason.SegmentMissingRequestedUpgradeCabin]: notAvailableErr,
        [UpgradeStatusReason.TicketNotIssuedByFinnair]: notAvailableErr,
        [UpgradeStatusReason.IneligibleBookingClass]: notAvailableErr,
        [UpgradeStatusReason.BookingInIneligibleStatus]: notAvailableErr,
        [UpgradeStatusReason.FlownSegment]: notAvailableErr,
      };

      return errorMap[upgrade.upgradeStatusReason] || notAvailableErr;
    }
  }

  getUpgradePaymentOptions(cabin: Cabin, flight: FlightV2): PaymentOptionsV2 {
    const upgrade = getUpgradeByCabin(cabin, flight.upgrades);

    return upgrade?.paymentOptions;
  }

  emitTabChange(event: { index: number; data: unknown }, flightId: string): void {
    this.activeTab[flightId] = event.data as Cabin;
    this.changeTabMethod.emit({ cabin: event.data as Cabin, flightId });
  }

  shouldRenderTabs(upgrades: Upgrade[]): boolean {
    const business = getUpgradeByCabin(Cabin.Business, upgrades);
    const premiumEconomy = getUpgradeByCabin(Cabin.Ecopremium, upgrades);

    const noBusinessUpgrade = business ? business.upgradeStatus === UpgradeStatus.NotAvailable : true;
    const noPremiumEconomyUpgrade = premiumEconomy ? premiumEconomy.upgradeStatus === UpgradeStatus.NotAvailable : true;

    return !(noBusinessUpgrade && noPremiumEconomyUpgrade);
  }

  getUpgradeByCabin(cabin: Cabin, upgrades: Upgrade[]): Upgrade {
    return getUpgradeByCabin(cabin, upgrades);
  }

  getSeatsAvailableText(upgrade: Upgrade): insufficientSeats {
    return upgrade.seatsAvailable === 1
      ? 'oneUpgrade.notifications.insufficientSeats.singular'
      : 'oneUpgrade.notifications.insufficientSeats.plural';
  }

  showWaitlistInfo(cabin: Cabin, flight: FlightV2): boolean {
    const upgrade = getUpgradeByCabin(cabin, flight.upgrades);

    return upgrade.isWaitlist;
  }

  moneyAsLast(a: KeyValue<PaymentType, PaymentOptionV2>, b: KeyValue<PaymentType, PaymentOptionV2>): number {
    return sortByPaymentType(a.key, b.key);
  }

  openModal(event: Event, flightId: string, productName: UpgradeProduct): void {
    event.stopPropagation();

    this.store$.dispatch(
      ModalActions.openModal({
        modal: {
          title: 'oneUpgrade.upgradeSelection.waitlistInfoModal.title',
          fragment: 'fragments.oneUpgrade.waitlistHelp.url',
          buttons: ModalButtons.DONE,
        },
        returnTarget: `[data-flight="${flightId}"][data-upgrade="${productName}"] fcom-notification button`,
      })
    );
  }

  trackByFn(_index: number, item: Flight): string {
    return item.segmentInfo.flightId;
  }

  emitChange(
    selected: boolean,
    elemIndex: number,
    flight: FlightV2,
    option: KeyValue<PaymentType, PaymentOptionV2>,
    upgradeProduct: UpgradeProduct
  ): void {
    this.changePaymentMethod.emit({
      selected,
      elemIndex,
      flightId: flight.segmentInfo.flightId,
      paymentOption: option.value,
      paymentType: option.key,
      group: flight.segmentInfo.group,
      longHaul: flight.segmentInfo.longHaul,
      upgradeProduct,
    });
  }

  isDisabled(flightId: string, cabin: Cabin, index: number): boolean {
    return (this.paymentForm.controls[flightId] as UntypedFormArray).controls[cabin].controls[index].disabled;
  }

  getTooltip(
    flight: FlightV2,
    productName: UpgradeProduct,
    paymentType: PaymentType,
    index: number
  ): Observable<TooltipType> {
    const isInsufficientPayment = this.upgradePaymentService.insufficientPayment(flight, productName, paymentType);
    const isDisabled = this.isDisabled(flight.segmentInfo.flightId, getCabinFromUpgradeName(productName), index);
    const upgradeBenefits = [PaymentType.EUROPE_UPGRADE, PaymentType.UPGRADE_BENEFIT, PaymentType.LONG_HAUL_UPGRADE];

    return combineLatest([this.originalBalance$, this.passengers$]).pipe(
      map(([originalBalance, paxAmount]) => {
        if (paymentType === PaymentType.money && !isDisabled) {
          /**
           * Tooltip disabled as card payments are now allowed in some situations.
           * https://trello.com/c/qXDFjyFi/9235-hide-card-payment-pop-up-and-info-button-from-upgrade-flow
           **/
          return null;
        }

        if (paymentType === PaymentType.points && isInsufficientPayment) {
          return TooltipType.NotEnoughPoints;
        }

        if (
          paymentType === PaymentType.LONG_HAUL_UPGRADE &&
          isDisabled &&
          originalBalance.LONG_HAUL_UPGRADE < paxAmount
        ) {
          return TooltipType.NotEnoughLongHaulVouchersForPax;
        }

        if (upgradeBenefits.includes(paymentType) && isInsufficientPayment) {
          return TooltipType.NotEnoughVouchers;
        }

        return TooltipType.CantCombine;
      })
    );
  }
}
